import React from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  Divider,
  Paper,
  Alert,
} from '@mui/material';
import {
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  Download as DownloadIcon,
  LocalShipping as LocalShippingIcon,
} from '@mui/icons-material';
import ProductImage from '../ProductImage';
import {
  formatDate,
  formatPrice,
  getStatusColor,
  getStatusIcon,
  getStatusLabel,
  canCancelOrder
} from '../../utils/orderUtils';

/**
 * Composant pour le modal des détails de commande
 */
const OrderDetailsModal = ({
  open,
  order,
  onClose,
  onCancelOrder,
  onValidateOrder,
  onDownloadInvoice,
  onMarkAsShipped
}) => {
  if (!order) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: '90vh',
          borderRadius: 2,
          backgroundImage: 'none',
          backgroundColor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
        }
      }}
    >
      <DialogTitle sx={{
        borderBottom: '1px solid',
        borderColor: 'divider',
        pb: 2,
        mb: 0,
        fontWeight: 600,
        fontSize: '1.25rem'
      }}>
        Détails de la commande {order.orderNumber}
      </DialogTitle>
      <DialogContent sx={{
        p: 0,
        '&::-webkit-scrollbar': {
          width: '8px',
        },
        '&::-webkit-scrollbar-track': {
          background: 'rgba(0,0,0,0.1)',
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: 'rgba(255, 255, 255, 0.3)',
          borderRadius: '4px',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          '&:hover': {
            background: 'rgba(255, 255, 255, 0.5)',
          },
        },
        '&::-webkit-scrollbar-corner': {
          background: 'transparent',
        },
        // Pour Firefox
        scrollbarWidth: 'thin',
        scrollbarColor: 'rgba(255, 255, 255, 0.3) rgba(0, 0, 0, 0.1)',
      }}>
        <Box sx={{ p: 3 }}>
          <Grid container spacing={3}>
            {/* Informations générales */}
            <Grid item xs={12}>
              <Card variant="outlined" sx={{
                backgroundColor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(255, 255, 255, 0.1)',
                  borderColor: 'text.secondary',
                },
                transition: 'box-shadow 0.3s ease',
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{
                    color: 'primary.main',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}>
                    Informations générales
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Statut:
                      </Typography>
                      <Chip
                        icon={getStatusIcon(order.status)}
                        label={getStatusLabel(order.status)}
                        color={getStatusColor(order.status)}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Date de commande:
                      </Typography>
                      <Typography variant="body2">
                        {formatDate(order.createdAt)}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Articles commandés */}
            <Grid item xs={12}>
              <Card variant="outlined" sx={{
                backgroundColor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(255, 255, 255, 0.1)',
                  borderColor: 'text.secondary',
                },
                transition: 'box-shadow 0.3s ease',
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{
                    color: 'primary.main',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}>
                    Articles commandés
                  </Typography>
                  {order.items && order.items.length > 0 ? (
                    order.items.map((item, index) => {
                      const unitPriceTtc = item.unitPrice ?? 0
                      const totalPriceTtc = item.totalPrice ?? unitPriceTtc * item.quantity
                      const unitPriceHt = item.unitPriceHt ?? (item.totalPriceHt ? item.totalPriceHt / item.quantity : (unitPriceTtc ? unitPriceTtc / 1.2 : 0))
                      const totalPriceHt = item.totalPriceHt ?? (unitPriceHt * item.quantity)

                      return (
                        <Box key={item.id || index} sx={{ mb: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                            {/* Image de l'article */}
                            <ProductImage
                              imageUrl={item.variantImage || item.productImage}
                              productName={item.productName}
                              size={60}
                              borderRadius={1}
                            />

                            {/* Informations de l'article */}
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="body2" fontWeight="medium" sx={{ mb: 0.5 }}>
                                {item.productName}
                                {item.variantName && ` - ${item.variantName}`}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                Quantité: {item.quantity}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                HT: {formatPrice(unitPriceHt)} × {item.quantity} = {formatPrice(totalPriceHt)}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                TTC: {formatPrice(unitPriceTtc)} × {item.quantity} = {formatPrice(totalPriceTtc)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Prix unitaire: {formatPrice(unitPriceHt)} HT ({formatPrice(unitPriceTtc)} TTC)
                              </Typography>
                            </Box>
                          </Box>
                          {index < order.items.length - 1 && <Divider sx={{ mt: 2 }} />}
                        </Box>
                      )
                    })
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Aucun article trouvé dans cette commande.
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Adresses */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{
                backgroundColor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(255, 255, 255, 0.1)',
                  borderColor: 'text.secondary',
                },
                transition: 'box-shadow 0.3s ease',
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{
                    color: 'primary.main',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}>
                    Adresse de livraison
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>{order.deliveryFirstName} {order.deliveryLastName}</strong>
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {order.deliveryAddress}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {order.deliveryPostalCode} {order.deliveryCity}
                  </Typography>
                  <Typography variant="body2">
                    {order.deliveryCountry}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{
                backgroundColor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(255, 255, 255, 0.1)',
                  borderColor: 'text.secondary',
                },
                transition: 'box-shadow 0.3s ease',
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{
                    color: 'primary.main',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}>
                    Adresse de facturation
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>{order.billingFirstName} {order.billingLastName}</strong>
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {order.billingAddress}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {order.billingPostalCode} {order.billingCity}
                  </Typography>
                  <Typography variant="body2">
                    {order.billingCountry}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Informations de livraison */}
            <Grid item xs={12}>
              <Card variant="outlined" sx={{
                backgroundColor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(255, 255, 255, 0.1)',
                  borderColor: 'text.secondary',
                },
                transition: 'box-shadow 0.3s ease',
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{
                    color: 'primary.main',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}>
                    🚚 Informations de livraison
                  </Typography>
                  <Grid container spacing={2}>
                    {/* Debug temporaire */}
                    {console.log('🔍 Order shipping data in modal:', {
                      orderId: order.id,
                      shippingMethodCode: order.shippingMethodCode,
                      shippingPrice: order.shippingPrice,
                      shippingMethodDetails: order.shippingMethodDetails,
                      deliveryPhone: order.deliveryPhone,
                      pickupPointInfo: order.pickupPointInfo,
                      hasAnyShippingData: !!(order.shippingMethodCode || order.shippingPrice || order.deliveryPhone || order.pickupPointInfo)
                    })}

                    {(order.shippingMethodCode || order.shippingPrice || order.deliveryPhone || order.pickupPointInfo) ? (
                      <>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">
                            Mode de livraison:
                          </Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {order.shippingMethodDetails?.icon || '🚚'} {order.shippingMethodDetails?.name || order.shippingMethodCode || 'Non spécifié'}
                          </Typography>
                          {order.shippingPrice && (
                            <Typography variant="caption" color="text.secondary">
                              Prix: {formatPrice(order.shippingPrice)}
                            </Typography>
                          )}
                        </Grid>
                      </>
                    ) : (
                      <Grid item xs={12}>
                        <Alert severity="info" sx={{ mb: 2 }}>
                          <Typography variant="body2">
                            Cette commande a été passée avant l'implémentation du système de livraison détaillé.
                            Les informations de livraison ne sont pas disponibles.
                          </Typography>
                        </Alert>
                      </Grid>
                    )}
                    {order.deliveryPhone && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          Téléphone de livraison:
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          📞 {order.deliveryPhone}
                        </Typography>
                      </Grid>
                    )}
                    {order.pickupPointInfo && (
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Point relais sélectionné:
                        </Typography>
                        <Paper sx={{ p: 2, backgroundColor: 'action.hover' }}>
                          <Typography variant="body2" fontWeight="bold">
                            📍 {order.pickupPointInfo.name || order.pickupPointInfo.nom}
                          </Typography>
                          <Typography variant="body2">
                            {order.pickupPointInfo.address || order.pickupPointInfo.adresse1}
                          </Typography>
                          <Typography variant="body2">
                            {order.pickupPointInfo.postalCode || order.pickupPointInfo.codePostal} {order.pickupPointInfo.city || order.pickupPointInfo.localite}
                          </Typography>
                          {order.pickupPointInfo.distance && (
                            <Typography variant="caption" color="primary">
                              📏 Distance: {order.pickupPointInfo.distance} km
                            </Typography>
                          )}
                        </Paper>
                      </Grid>
                    )}
                    {order.trackingNumber && (
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                          Numéro de suivi:
                        </Typography>
                        <Chip
                          label={order.trackingNumber}
                          color="info"
                          size="small"
                          sx={{ fontFamily: 'monospace' }}
                        />
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Résumé financier */}
            <Grid item xs={12}>
              <Card variant="outlined" sx={{
                backgroundColor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(255, 255, 255, 0.1)',
                  borderColor: 'text.secondary',
                },
                transition: 'box-shadow 0.3s ease',
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{
                    color: 'primary.main',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}>
                    Résumé financier
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Sous-total HT:
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {formatPrice(order.subtotal)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Frais de port:
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {formatPrice(order.shippingCost)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        TVA:
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {formatPrice(order.taxAmount)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Total TTC:
                      </Typography>
                      <Typography variant="body2" fontWeight="bold" color="primary.main">
                        {formatPrice(order.totalAmount)}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Notes */}
            {order.notes && (
              <Grid item xs={12}>
                <Card variant="outlined" sx={{
                  backgroundColor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(255, 255, 255, 0.1)',
                    borderColor: 'text.secondary',
                  },
                  transition: 'box-shadow 0.3s ease',
                }}>
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="h6" gutterBottom sx={{
                      color: 'primary.main',
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}>
                      Notes
                    </Typography>
                    <Typography variant="body2">
                      {order.notes}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions sx={{
        borderTop: '1px solid',
        borderColor: 'divider',
        p: 2,
        gap: 1,
        flexWrap: 'wrap',
      }}>
        {/* Bouton Valider la commande (si status = pending) */}
        {order.status === 'pending' && onValidateOrder && (
          <Button
            variant="contained"
            color="success"
            startIcon={<CheckCircleIcon />}
            onClick={() => onValidateOrder(order.id)}
            sx={{
              backgroundColor: 'success.main',
              '&:hover': {
                backgroundColor: 'success.dark',
              },
            }}
          >
            Valider la commande
          </Button>
        )}

        {/* Bouton Télécharger la facture (si status = confirmed) */}
        {order.status === 'confirmed' && onDownloadInvoice && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<DownloadIcon />}
            onClick={() => onDownloadInvoice(order.id)}
            sx={{
              backgroundColor: 'primary.main',
              '&:hover': {
                backgroundColor: 'primary.dark',
              },
            }}
          >
            Télécharger la facture
          </Button>
        )}

        {/* Bouton Marquer comme envoyé (si status = confirmed ou processing) */}
        {['confirmed', 'processing'].includes(order.status) && onMarkAsShipped && (
          <Button
            variant="contained"
            color="secondary"
            startIcon={<LocalShippingIcon />}
            onClick={() => onMarkAsShipped(order.id)}
            sx={{
              backgroundColor: 'secondary.main',
              '&:hover': {
                backgroundColor: 'secondary.dark',
              },
            }}
          >
            Marquer comme envoyé
          </Button>
        )}

        {canCancelOrder(order) && (
          <Button
            variant="contained"
            color="error"
            startIcon={<CancelIcon />}
            onClick={() => onCancelOrder(order.id)}
            sx={{
              backgroundColor: 'error.main',
              '&:hover': {
                backgroundColor: 'error.dark',
              },
            }}
          >
            Annuler la commande
          </Button>
        )}
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{
            borderColor: 'text.secondary',
            color: 'text.secondary',
            '&:hover': {
              borderColor: 'text.primary',
              color: 'text.primary',
            },
          }}
        >
          Fermer
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OrderDetailsModal;
